import { useCallback, useEffect, useState } from 'react';
import { Button } from '@ludolio/shared-ui';
import { useLabsReview } from './context';
import { LabsReviewDetail } from './LabsReviewDetail';
import { formatDate, platformSummary, visibilityLabel } from './format';
import type { LabReviewQueueItem, LabReviewScope } from './types';

export function LabsReviewQueue({ scope, emptyMessage }: { scope: LabReviewScope; emptyMessage: string }) {
  const { api, getErrorMessage } = useLabsReview();
  const [items, setItems] = useState<LabReviewQueueItem[] | null>(null);
  const [error, setError] = useState('');
  const [openReleaseId, setOpenReleaseId] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .listQueue(scope)
      .then((result) => {
        setItems(result ?? []);
        setError('');
      })
      .catch((err) => setError(getErrorMessage(err)));
  }, [api, getErrorMessage, scope]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {!items && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {items?.length === 0 && (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">{emptyMessage}</div>
      )}
      {items && items.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {items.map((item) => (
            <li key={item.releaseId} className="grid gap-3 p-4 sm:grid-cols-[minmax(0,1fr)_10rem_8rem_auto] sm:items-center">
              <div className="flex min-w-0 items-center gap-3">
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border bg-muted">
                  {item.iconUrl && <img src={item.iconUrl} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <p className="truncate font-medium">{item.title} · {item.version}</p>
                  <p className="truncate text-sm text-muted-foreground">
                    {[item.unit?.code, item.submittedBy, platformSummary(item.platforms)].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
              <span className="text-sm text-muted-foreground">{visibilityLabel(item.visibility)}</span>
              <span className="text-sm text-muted-foreground">{formatDate(item.submittedAt)}</span>
              <Button size="sm" variant="outline" onClick={() => setOpenReleaseId(item.releaseId)}>Review</Button>
            </li>
          ))}
        </ul>
      )}
      <LabsReviewDetail releaseId={openReleaseId} onClose={() => setOpenReleaseId(null)} onDecided={load} />
    </>
  );
}
